import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomAlert from '../components/CustomAlert';

/**
 * Custom hook for checking transcription limits and displaying upgrade alerts
 * @returns {Object} - Object containing checkLimit function and LimitAlert component
 */
export const useTranscriptionLimit = () => {
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [limitAlertMessage, setLimitAlertMessage] = useState('');
  const navigate = useNavigate();

  /**
   * Check if user has reached transcription limit
   * @returns {Promise<boolean>} - Returns true if user can proceed, false if limit reached
   */
  const checkLimit = useCallback(async () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) return true;

    try {
      const usageResponse = await fetch('/api/usage/current', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        if (usageData.limitReached) {
          const tier = usageData.tier || 'free';
          const limit = usageData.limits?.monthlyTranscriptions || 'your';

          let message = '';
          if (tier === 'free') {
            message = `You've reached your monthly limit of ${limit} transcriptions. Please upgrade to continue.`;
          } else if (tier === 'basic') {
            message = `You've reached your monthly limit of ${limit} transcriptions. Please upgrade to Premium for unlimited transcriptions.`;
          } else {
            message = 'You have reached your transcription limit. Please check your subscription.';
          }

          setLimitAlertMessage(message);
          setShowLimitAlert(true);
          return false;
        }
      }
      return true;
    } catch (err) {
      console.error('Error checking limits:', err);
      return true;
    }
  }, []);

  /**
   * CustomAlert component for displaying limit reached message
   */
  const LimitAlert = () => (
    showLimitAlert ? (
      <CustomAlert
        isOpen={true}
        type="warning"
        title="Transcription Limit Reached"
        message={limitAlertMessage}
        confirmText="Upgrade Now"
        onConfirm={() => {
          setShowLimitAlert(false);
          navigate('/plans-and-payment');
        }}
        showCancel={true}
        cancelText="Close"
        onClose={() => setShowLimitAlert(false)}
      />
    ) : null
  );

  return { checkLimit, LimitAlert };
};
